import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import ArticleCard from "@/components/news/ArticleCard";
import { Reveal } from "@/components/motion/Reveal";
import { articleBySlug, articlesByCategory, categoryById, formatDate } from "@/data/articles";
import { services } from "@/data/services";
import { trackEvent } from "@/lib/analytics";
import Seo, { breadcrumbLd } from "@/components/seo/Seo";

const SITE = process.env.REACT_APP_SITE_URL || "https://autobasiccare.com";

function Block({ b }) {
  if (b.t === "h2") return <h2 className="article-h2">{b.c}</h2>;
  if (b.t === "ul") return <ul className="article-ul">{b.c.map((li) => <li key={li}>{li}</li>)}</ul>;
  if (b.t === "tip") return <aside className="article-tip"><span className="article-tip-label">Shop Tip</span><p>{b.c}</p></aside>;
  return <p className="article-p">{b.c}</p>;
}

export default function Article() {
  const { slug } = useParams();
  const article = articleBySlug(slug);
  if (!article) return <Navigate to="/news" replace />;

  const cat = categoryById(article.category);
  const service = services.find((s) => s.name === article.bookingService);
  const related = articlesByCategory(article.category).filter((a) => a.slug !== slug).slice(0, 3);
  const more = related.length ? related : articlesByCategory("brakes").slice(0, 3);

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    dateModified: article.date,
    author: { "@type": "Organization", name: "Auto Basic Care" },
    publisher: { "@type": "Organization", name: "Auto Basic Care", url: SITE },
    articleSection: cat?.label,
    mainEntityOfPage: `${SITE}/news/${article.slug}`,
  };

  return (
    <div className="abc-app">
      <Seo
        path={`/news/${article.slug}`}
        title={article.title}
        description={article.excerpt}
        image={article.image}
        type="article"
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "News", path: "/news" },
            { name: cat?.label || "Article", path: `/news?cat=${article.category}` },
            { name: article.title, path: `/news/${article.slug}` },
          ]),
          articleLd,
        ]}
      />
      <SiteHeader />

      <article className="article-page" data-testid="article-page">
        <header className="article-hero">
          <div className="container article-hero-inner">
            <nav className="article-crumbs" aria-label="Breadcrumb" data-testid="article-breadcrumbs">
              <Link to="/news">News</Link>
              <span aria-hidden="true">/</span>
              <Link to={`/news?cat=${article.category}`} data-testid="article-cat-link">{cat?.label}</Link>
            </nav>
            <h1 className="article-title" data-testid="article-title">{article.title}</h1>
            {article.subtitle && <p className="article-subtitle">{article.subtitle}</p>}
            <div className="article-meta" data-testid="article-meta">
              <span>{article.author}</span>
              <span aria-hidden="true">·</span>
              <time dateTime={article.date}>{formatDate(article.date)}</time>
              <span aria-hidden="true">·</span>
              <span>{article.readTime} read</span>
            </div>
          </div>
        </header>

        <div className="container">
          <figure className="article-figure">
            <img
              src={article.image}
              srcSet={article.imageSrcSet}
              sizes="(min-width: 1200px) 1200px, 100vw"
              alt={article.imageAlt}
              width="1400"
              height="800"
              fetchPriority="high"
            />
          </figure>
        </div>

        <div className="container article-layout">
          <div className="article-body" data-testid="article-body">
            {article.body.map((b, i) => <Block key={i} b={b} />)}

            <div className="article-endcap">
              <div className="eyebrow">Sound familiar?</div>
              <p>Book a {article.bookingService} online or call 205-405-1238 and talk to a technician.</p>
              <div className="button-row">
                <Link
                  to="/#schedule"
                  className="btn btn-primary"
                  data-testid="article-end-book"
                  onClick={() => trackEvent("InitiateCheckout", { content_type: "article", content_name: article.title })}
                >
                  Book {article.bookingService} →
                </Link>
                <Link to="/news" className="btn btn-outline-dark" data-testid="article-end-back">All Articles</Link>
              </div>
            </div>
          </div>

          <aside className="article-side">
            <div className="article-side-card" data-testid="article-service-card">
              <div className="eyebrow">Related Service</div>
              <h3>{article.bookingService}</h3>
              {service && (
                <p className="article-side-price">
                  {service.price === 0 ? "Free" : `$${service.price}`} <span>· {service.time}</span>
                </p>
              )}
              <Link
                to="/#schedule"
                className="btn btn-primary"
                data-testid="article-side-book"
                onClick={() => trackEvent("InitiateCheckout", { content_type: "article-side", content_name: article.bookingService })}
              >
                Schedule Now
              </Link>
              <a href="tel:2054051238" className="article-side-call" data-testid="article-side-call">Or call 205-405-1238</a>
            </div>

            <div className="article-side-card article-side-cat">
              <div className="eyebrow">Category</div>
              <h3>{cat?.label}</h3>
              <p className="muted">{cat?.blurb}</p>
              <Link to={`/news?cat=${article.category}`} className="news-card-read" data-testid="article-side-cat-link">
                More {cat?.label} articles →
              </Link>
            </div>
          </aside>
        </div>

        <section className="section article-related" style={{ background: "var(--gray-soft)" }}>
          <div className="container">
            <div className="section-header">
              <div>
                <div className="eyebrow">Keep Reading</div>
                <h2 className="section-title">{related.length ? `More on ${cat?.label}` : "From the forum"}</h2>
              </div>
              <Link to="/news" className="news-group-link" data-testid="article-related-all">All articles →</Link>
            </div>
            <div className="news-grid" data-testid="article-related-grid">
              {more.map((a, i) => (
                <Reveal key={a.slug} delay={i * 0.06}>
                  <ArticleCard article={a} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      </article>

      <SiteFooter />
    </div>
  );
}
